<template>
  <div ref="triggerRef" class="product-picker">
    <label class="block text-sm font-medium text-slate-700 mb-1">
      产品 <span class="text-red-500">*</span>
    </label>
    <div
      class="picker-trigger form-select flex items-center cursor-pointer"
      :class="{ 'is-open': open }"
      @click="toggle"
    >
      <span v-if="selectedProduct" class="text-sm text-slate-900 truncate flex-1">
        {{ selectedProduct.name }} ({{ selectedProduct.code }})
      </span>
      <span v-else class="text-sm text-slate-400 flex-1">请选择产品</span>
      <svg
        class="w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0"
        :class="{ 'rotate-180': open }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>

    <Teleport to="body">
      <div v-if="open" class="fixed inset-0 z-[9999]" @click="close">
        <div
          class="picker-panel absolute bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          :style="panelStyle"
          @click.stop
        >
          <div class="flex h-full">
            <!-- Left: Category Sidebar -->
            <div class="category-sidebar w-[180px] flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col">
              <div class="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 flex items-center min-h-[60px]">
                产品分类
              </div>
              <div class="flex-1 overflow-y-auto">
                <div
                  class="category-item px-3 py-2.5 text-sm cursor-pointer transition-colors border-l-2"
                  :class="selectedCategoryId === null
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-transparent text-slate-700 hover:bg-slate-100'"
                  @click="selectCategory(null)"
                >
                  <div class="flex items-center gap-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                    <span>全部产品</span>
                  </div>
                </div>
                <div
                  v-for="cat in categories"
                  :key="cat.id"
                  class="category-item px-3 py-2.5 text-sm cursor-pointer transition-colors border-l-2"
                  :class="selectedCategoryId === cat.id
                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                    : 'border-transparent text-slate-700 hover:bg-slate-100'"
                  @click="selectCategory(cat.id)"
                >
                  <div class="flex items-center gap-2">
                    <div class="w-4 h-4 flex items-center justify-center">
                      <img
                        v-if="cat.icon && !isSvgPath(cat.icon)"
                        :src="resolveFileUrl(cat.icon)"
                        class="w-4 h-4 object-cover rounded"
                        alt=""
                      >
                      <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path :d="(cat.icon && isSvgPath(cat.icon)) ? cat.icon : 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                      </svg>
                    </div>
                    <span class="truncate">{{ cat.name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Product Area -->
            <div class="product-area flex-1 flex flex-col min-w-0">
              <!-- Search -->
              <div class="px-4 py-3 border-b border-slate-200 flex items-center gap-2 min-h-[60px]">
                <div class="relative flex-1 min-w-0">
                  <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    v-model="searchKeyword"
                    type="text"
                    placeholder="搜索产品名称或编码"
                    class="form-search-input form-control-sm w-full"
                    @input="onSearchInput"
                    @keyup.enter="onSearchInput"
                  >
                </div>
                <button
                  type="button"
                  class="flex-shrink-0 px-2.5 py-1.5 text-xs bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors inline-flex items-center gap-1"
                  @click="handleAddTempProduct"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                  新建
                </button>
              </div>

              <!-- Product List -->
              <div class="flex-1 overflow-y-auto">
                <div v-if="loading" class="flex items-center justify-center py-12">
                  <div class="flex items-center gap-2 text-sm text-slate-500">
                    <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    加载中...
                  </div>
                </div>

                <div v-else-if="products.length === 0" class="px-4 py-8 text-center">
                  <p class="text-sm text-slate-500 mb-3">
                    未找到「{{ searchKeyword || '相关产品' }}」
                  </p>
                  <button
                    v-if="searchKeyword"
                    type="button"
                    class="px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-sm hover:bg-primary-100 transition-colors inline-flex items-center gap-1.5"
                    @click="handleAddTempProduct"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    创建产品「{{ searchKeyword }}」
                  </button>
                </div>

                <div v-else class="divide-y divide-slate-100">
                  <div
                    v-for="p in products"
                    :key="p.id"
                    class="product-item px-4 py-3 cursor-pointer transition-colors hover:bg-primary-50"
                    :class="modelValue === p.id ? 'bg-primary-50 border-l-2 border-l-primary-500' : ''"
                    @click="selectProduct(p)"
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex-1 min-w-0">
                        <div class="text-sm font-medium text-slate-900 truncate">{{ p.name }}</div>
                        <div class="text-xs text-slate-500 mt-0.5">{{ p.code }}</div>
                      </div>
                      <svg
                        v-if="modelValue === p.id"
                        class="w-5 h-5 text-primary-600 flex-shrink-0 ml-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Selected indicator -->
              <div v-if="selectedProduct" class="px-4 py-2 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
                已选：<span class="font-medium text-slate-700">{{ selectedProduct.name }} ({{ selectedProduct.code }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getProductPage, createTemporaryProduct } from '@/api/product'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  categories: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const triggerRef = ref(null)
const open = ref(false)
const loading = ref(false)
const searchKeyword = ref('')
const selectedCategoryId = ref(null)
const products = ref([])

let searchTimer = null

const selectedProduct = computed(() => {
  if (!props.modelValue) return null
  return products.value.find(p => p.id === props.modelValue) || null
})

const panelStyle = computed(() => {
  if (!triggerRef.value || !open.value) return {}
  const rect = triggerRef.value.getBoundingClientRect()
  const panelWidth = 620
  const panelHeight = 400
  const gap = 4

  let top = rect.bottom + gap
  let left = rect.left

  // Flip if not enough space below
  if (top + panelHeight > window.innerHeight - 16) {
    top = rect.top - panelHeight - gap
  }

  // Adjust horizontal to stay in viewport
  if (left + panelWidth > window.innerWidth - 16) {
    left = window.innerWidth - panelWidth - 16
  }
  if (left < 16) left = 16

  return {
    top: `${top}px`,
    left: `${left}px`,
    width: `${panelWidth}px`,
    height: `${panelHeight}px`
  }
})

const toggle = () => {
  open.value = !open.value
  if (open.value) {
    searchKeyword.value = ''
    selectedCategoryId.value = null
    loadProducts()
  }
}

const close = () => {
  open.value = false
}

const selectCategory = (categoryId) => {
  selectedCategoryId.value = categoryId
  loadProducts()
}

const selectProduct = (product) => {
  emit('update:modelValue', product.id)
  emit('change', product)
  open.value = false
}

const loadProducts = async () => {
  loading.value = true
  try {
    // Search across all categories when keyword is present
    const hasKeyword = searchKeyword.value && searchKeyword.value.trim()
    const data = await getProductPage({
      categoryId: hasKeyword ? undefined : (selectedCategoryId.value || undefined),
      keyword: hasKeyword || undefined,
      page: 1,
      size: 100
    })
    products.value = data.records || []
  } catch (error) {
    console.error('加载产品列表失败', error)
    products.value = []
  } finally {
    loading.value = false
  }
}

const onSearchInput = () => {
  // Switch to "all products" when searching
  if (searchKeyword.value && searchKeyword.value.trim()) {
    selectedCategoryId.value = null
  }
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    loadProducts()
  }, 300)
}

const handleAddTempProduct = async () => {
  if (!searchKeyword.value) {
    ElMessage.warning('请输入产品名称')
    return
  }
  try {
    await ElMessageBox.confirm(
      `确认创建临时产品「${searchKeyword.value}」？`,
      '创建产品',
      {
        confirmButtonText: '确认创建',
        cancelButtonText: '取消',
        type: 'info'
      }
    )
    const newProduct = await createTemporaryProduct({
      name: searchKeyword.value
    })
    ElMessage.success('产品创建成功')
    products.value.unshift({ id: newProduct.id, name: newProduct.name, code: newProduct.code })
    emit('update:modelValue', newProduct.id)
    emit('change', { id: newProduct.id, name: newProduct.name, code: newProduct.code })
    searchKeyword.value = ''
    open.value = false
  } catch (error) {
    if (error === 'cancel' || error === 'close') return
    console.error('创建产品失败', error)
    ElMessage.error('创建产品失败')
  }
}

const isSvgPath = (icon) => {
  if (!icon) return false
  return /^[MLHVCSQTAZmlhvcsqtaz\d\s.,-]+$/.test(icon.trim())
}

const resolveFileUrl = (fileKey) => {
  if (!fileKey) return ''
  if (/^https?:\/\//i.test(fileKey)) return fileKey
  return `${process.env.VUE_APP_API_BASE_URL || ''}/${fileKey}`.replace(/\/+/g, '/').replace(':/', '://')
}

watch(() => props.modelValue, (newVal) => {
  // If modelValue was cleared externally, reset
  if (!newVal) {
    searchKeyword.value = ''
  }
})

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>
